import React, { useState } from 'react';
import PageHero from '../components/PageHero';
import CtaSection from '../components/CtaSection';
import { CodePanel } from '../components/Statement';
import { Hairline, Label, Reveal, Section, SectionHead } from '../components/System';
import useSeo from '../lib/useSeo';

const ANALYSIS_TYPES = ['total', 'breakdown', 'ranking', 'trend', 'variance', 'driver_analysis', 'waterfall', 'completeness'];

const PATHS = {
  A: {
    label: 'Finance data',
    tag: 'FinanceEngine',
    file: 'finance_engine.py',
    detail: 'For standard budget and transaction tables. The AI plans. The engine does the maths.',
    code: `plan = FinancialAnalysisPlan(
    analysis_type="variance",
    dimension="department_name",
    period=("2026-04-01", "2026-06-30"),
)
result = FinanceEngine.run(plan)  # deterministic, no LLM math`,
  },
  B: {
    label: 'Any other data',
    tag: 'QueryExecutor',
    file: 'query_executor.py',
    detail: 'For every other table. The plan is built from your real columns.',
    code: `plan = AnalysisPlan(
    analysis_type="ranking",
    dimension=field("vendor"),
    measure=field("actual_amount"),
    limit=5,
)
sql = QueryExecutor.compile(plan, dataset)  # validated pushdown SQL`,
  },
};

const STAGES = [
  { title: 'Understand', detail: 'Your question is turned into a plan.' },
  { title: 'Check', detail: 'Every column is checked before anything runs.' },
  { title: 'Calculate', detail: 'Your database does the maths.' },
  { title: 'Explain', detail: 'A short summary, tied to the calculation.' },
];

function PushdownSQLEngine() {
  const [path, setPath] = useState('A');
  const current = PATHS[path];

  useSeo({
    title: 'Pushdown SQL Engine for Financial Analysis',
    path: '/pushdown-sql-engine',
    description: 'Sidwell runs every calculation as SQL inside your own database. Your data stays put, only the answer comes back, and the AI never touches a number.',
  });

  return (
    <main className="relative w-full">
      <PageHero
        image="between"
        eyebrow="Pushdown SQL Engine"
        title="The maths runs in your database."
        subtitle="Your data stays put. Only the answer comes back."
        badge="No data copied"
        primary={{ label: 'Request a demo', to: '/#request-briefing' }}
      />

      <Section tone="white" className="lg:pt-[calc(clamp(56px,6vw,88px)+64px)]">
        <div className="shell">
          <SectionHead label="The pipeline" title="Four steps. No guesswork." aside="Every answer follows the same path." />
          {/* Engine flow: one continuous line, four stations. */}
          <div className="mt-12 lg:mt-20">
            <Hairline tone="wall" className="hidden lg:block" />
            <ol className="m-0 grid list-none grid-cols-1 gap-x-[clamp(16px,2vw,28px)] p-0 lg:grid-cols-4">
              {STAGES.map((s, i) => (
                <Reveal as="li" key={s.title} delay={i * 70} className="relative border-t border-wall/25 py-6 lg:border-t-0 lg:pt-8">
                  <span aria-hidden="true" className="absolute left-0 top-[-4px] hidden h-[7px] w-[7px] bg-bronze lg:block" />
                  <h3 className="type-h3 !text-[clamp(26px,2.4vw,36px)] !font-light">{s.title}</h3>
                  <p className="mt-3 max-w-[28ch] text-glass">{s.detail}</p>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </Section>

      <Section tone="wall">
        <div className="shell">
          {/* The heading leads straight into the route picker, with the code alongside from the top. */}
          <div className="grid-12 gap-y-8">
            <div className="col-span-4 lg:col-span-5">
              <Label dark className="mb-5">Two routes</Label>
              <h2 className="type-h2">Pick a route.</h2>
              <p className="type-body mb-10 mt-5 text-glass-light">
                Finance data and any other data take different routes to the same exact answer.
              </p>
              <div role="group" aria-label="Routes">
              {Object.entries(PATHS).map(([key, p]) => {
                const on = path === key;
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setPath(key)}
                    className={`relative block min-h-[48px] w-full border-t border-limestone/25 py-6 pl-5 pr-4 text-left transition-colors last:border-b ${
                      on ? 'bg-spandrel' : 'hover:bg-spandrel/50'
                    }`}
                  >
                    <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-[3px] ${on ? 'bg-bronze' : 'bg-transparent'}`} />
                    <span className="block font-display text-[clamp(26px,2.4vw,36px)] font-light leading-tight">{p.label}</span>
                  </button>
                );
              })}
              </div>
              <p key={path} className="swap-in mt-6 text-[clamp(18px,1.5vw,21px)] leading-snug">{current.detail}</p>
            </div>
            <div className="col-span-4 min-w-0 lg:col-span-7">
              <CodePanel className="border border-limestone/20 !bg-spandrel" title={current.file} meta={current.tag} code={current.code} />
              <div className="mt-8">
                <Label dark muted className="mb-3">What it can work out</Label>
                <ul className="m-0 grid list-none grid-cols-2 gap-x-7 border-b border-limestone/20 p-0 sm:grid-cols-4">
                  {ANALYSIS_TYPES.map((type) => (
                    <li key={type} className="border-t border-limestone/20 py-2.5 font-mono text-[13px] text-limestone">
                      {type}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <CtaSection
        image="tower"
        eyebrow="Get Started"
        title="See it on your data."
        subtitle="Ask a real question. See exactly what ran."
        primary={{ label: 'Request a demo', to: '/#request-briefing' }}
        secondary={{ label: 'Back to Overview', to: '/' }}
      />
    </main>
  );
}

export default PushdownSQLEngine;
